'use client'

import { Search16 } from '@vinted/monochrome-icons'
import { Card, Cell, Divider, Image, InputBar, InputText } from '@vinted/web-ui'
import { noop } from 'lodash'

import {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { List } from '@marketplace-web/common-components/list-ui'
import {
  ListNavigator,
  ListNavigatorKeyboardKey,
} from '@marketplace-web/common-components/list-navigator-ui'
import { OutsideClick } from '@marketplace-web/shared/outside-click'
import { useAsset } from '@marketplace-web/domain/assets'
import {
  GooglePlacesAutocompleteRequestType,
  viewSuggestedPlacesEvent,
  selectSuggestedPlaceEvent,
} from '@marketplace-web/shipping/google-places-data'
import { shippingClickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'

import useGoogleSession from '../../hooks/useGoogleSession'
import { GoogleAutocompleteType } from '../../constants'
import { buildGooglePlaceDetailsRequest, getGooglePlacePredictions } from '../../utils'

type Props = {
  name: string
  type: GoogleAutocompleteType
  isLoading: boolean
  screen: Screen
  title?: string | JSX.Element
  placeholder?: string
  country?: string
  initialValue?: string | null
  validation?: ReactNode
  suggestionsListMaxHeight?: number
  queryDebounce?: number
  checkoutId?: string
  onValueChange?: (value: string | null, suggestion?: string) => void
  onPlaceDetailsChange?: (placeDetails: google.maps.places.PlaceResult) => void
}

const GooglePlacesAutocomplete = ({
  name,
  type = GoogleAutocompleteType.InputText,
  isLoading = false,
  screen,
  title,
  placeholder,
  country,
  initialValue,
  validation,
  suggestionsListMaxHeight,
  queryDebounce = 300,
  checkoutId,
  onValueChange = noop,
  onPlaceDetailsChange = noop,
}: Props) => {
  const { track } = useTracking()
  const asset = useAsset('/assets/google')

  const [value, setValue] = useState<string | null>(null)
  const [isSuggestionsShown, setIsSuggestionsShown] = useState(false)
  const [suggestions, setSuggestions] = useState<Array<google.maps.places.AutocompletePrediction>>(
    [],
  )

  const autocompleteInputRef = useRef<HTMLInputElement>(null)

  const sessionToken = useGoogleSession()

  useEffect(() => {
    if (initialValue) setValue(initialValue)
  }, [initialValue])

  const clearInputState = () => {
    setValue(null)
    setSuggestions([])
    setIsSuggestionsShown(false)
  }

  const handleOutsideClick = () => {
    if (!isSuggestionsShown) return

    onValueChange(value)
    setIsSuggestionsShown(false)
  }

  const setPlaceResult = (
    placeResult: google.maps.places.PlaceResult | null,
    status: google.maps.places.PlacesServiceStatus,
  ) => {
    if (status !== window.google.maps.places.PlacesServiceStatus.OK) return
    if (!placeResult) return

    onPlaceDetailsChange(placeResult)
  }

  const fetchPlaceDetails = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!autocompleteInputRef.current) return
    if (!sessionToken) return

    const map = new window.google.maps.Map(autocompleteInputRef.current)

    new window.google.maps.places.PlacesService(map).getDetails(
      buildGooglePlaceDetailsRequest(suggestion.place_id, sessionToken),
      setPlaceResult,
    )
  }

  const setSelectedSuggestion = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (value) onValueChange(value, suggestion.description)

    setValue(suggestion.description)
    setSuggestions([suggestion])
    setIsSuggestionsShown(false)
  }

  const trackPlaceSelect = (selectedSuggestion: google.maps.places.AutocompletePrediction) => {
    if (!sessionToken) return

    const sessionTokenString = sessionToken[Object.keys(sessionToken)[0]!]

    track(
      selectSuggestedPlaceEvent({
        selectedAutocomplete: selectedSuggestion.description,
        sessionToken: sessionTokenString,
        screen,
      }),
    )

    if (checkoutId) {
      track(
        shippingClickEvent({
          checkoutId,
          screen: Screen.PickupPointSearch,
          target: ClickableElement.PickupPointSearchResult,
          infoPii: JSON.stringify({
            selected_value: selectedSuggestion.description,
          }),
        }),
      )
    }
  }

  const handleSuggestionSelect = (highlightedResultIndex: number) => {
    const selectedSuggestion = suggestions[highlightedResultIndex]

    if (!selectedSuggestion) return

    fetchPlaceDetails(selectedSuggestion)
    setSelectedSuggestion(selectedSuggestion)
    trackPlaceSelect(selectedSuggestion)
  }

  const handleFocus = () => {
    if (checkoutId) {
      track(
        shippingClickEvent({
          checkoutId,
          screen: Screen.PickupPointSearch,
          target: ClickableElement.PickupPointSearchClick,
        }),
      )
    }

    if (!suggestions.length) return
    if (isSuggestionsShown) return

    setIsSuggestionsShown(true)
  }

  const handleKeyDown =
    (handleKeyNavigation: (event: KeyboardEvent) => void) => (event: KeyboardEvent) => {
      if (!isSuggestionsShown) return

      if (event.key === ListNavigatorKeyboardKey.Escape) setIsSuggestionsShown(false)
      if (event.key === ListNavigatorKeyboardKey.Tab) setIsSuggestionsShown(false)
      if (event.key === ListNavigatorKeyboardKey.Enter) {
        if (!suggestions[0]) return

        setValue(suggestions[0].description)
        fetchPlaceDetails(suggestions[0])
        setIsSuggestionsShown(false)
      }

      handleKeyNavigation(event)
    }

  const trackPlacesSuggestions = (query: string, count: number) => {
    if (!sessionToken) return

    const sessionTokenString = sessionToken[Object.keys(sessionToken)[0]!]

    track(
      viewSuggestedPlacesEvent({
        query,
        count,
        sessionToken: sessionTokenString,
        screen,
      }),
    )
  }

  const fetchSuggestionsCallback = (
    newSuggestions: Array<google.maps.places.AutocompletePrediction> | null,
  ) => {
    if (!newSuggestions?.length) return

    setSuggestions(newSuggestions)
    setIsSuggestionsShown(true)
  }

  const fetchSuggestions = useDebounce(async (inputValue: string) => {
    const autocompletionRequestParams = {
      value: inputValue,
      country,
      sessionToken,
      type: GooglePlacesAutocompleteRequestType.Geocode,
    }

    const nextPredictions = await getGooglePlacePredictions(autocompletionRequestParams)

    if (nextPredictions) trackPlacesSuggestions(inputValue, nextPredictions.length)

    fetchSuggestionsCallback(nextPredictions)
  }, queryDebounce)

  const handleChange = (event: ChangeEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>) => {
    const { value: inputValue } = event.currentTarget

    setValue(inputValue)

    if (inputValue) {
      fetchSuggestions(inputValue)
    } else {
      onValueChange(inputValue)

      setSuggestions([])
      setIsSuggestionsShown(false)
    }
  }

  const handleSuggestionClick = (suggestionIndex: number) => (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    handleSuggestionSelect(suggestionIndex)
  }

  const renderSuggestions = (highlightedResultIndex: number | null) => {
    if (!isSuggestionsShown) return null
    if (!suggestions.length) return null

    return (
      <div className="u-position-relative">
        <div className="u-position-absolute u-zindex-small u-fill-width">
          <Card styling={Card.Styling.Elevated}>
            <ScrollableArea maxHeight={suggestionsListMaxHeight}>
              <List>
                {suggestions.map((suggestion, index) => (
                  <Cell
                    key={suggestion.place_id}
                    type={Cell.Type.Navigating}
                    highlighted={suggestions.length > 1 && index === highlightedResultIndex}
                    title={suggestion.description}
                    onClick={handleSuggestionClick(index)}
                  />
                ))}
              </List>
            </ScrollableArea>
            <Divider />
            <Cell prefix={<Image src={asset('powered_by_google.png')} />} />
          </Card>
        </div>
      </div>
    )
  }

  const renderValidation = (highlightedResultIndex: number | null) => {
    return (
      <>
        {renderSuggestions(highlightedResultIndex)}
        {validation}
      </>
    )
  }

  const renderInput = (
    highlightedResultIndex: number | null,
    handleKeyNavigation: (event: KeyboardEvent) => void,
  ) => {
    const commonProps = {
      name,
      value,
      placeholder: placeholder ?? '',
      forwardedRef: autocompleteInputRef,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown(handleKeyNavigation),
      onChange: handleChange,
    }

    if (type === GoogleAutocompleteType.InputBar) {
      return (
        <>
          <InputBar
            {...commonProps}
            isLoading={isLoading}
            onValueClear={clearInputState}
            testId="autocomplete-input-bar"
            iconName={Search16}
          />
          {renderValidation(highlightedResultIndex)}
        </>
      )
    }

    return (
      <InputText
        {...commonProps}
        title={title}
        validation={renderValidation(highlightedResultIndex)}
      />
    )
  }

  return (
    <OutsideClick onOutsideClick={handleOutsideClick}>
      <ListNavigator itemCount={suggestions.length} onSelect={handleSuggestionSelect}>
        {({ highlightedResultIndex, handleKeyNavigation }) =>
          renderInput(highlightedResultIndex, handleKeyNavigation)
        }
      </ListNavigator>
    </OutsideClick>
  )
}

export default GooglePlacesAutocomplete
